import AnimationClip from '../AnimationClip';
import { Easing, Tween } from '@tweenjs/tween.js';
import ElementComponent from '../../../Component/UI/Element';
import GameObject from '../../../GameObject/GameObject';
import QuestButtonProgress from '../../../Prefabs/UI/HUD/Quests/QuestButtonProgress';
import { delay } from '../../../Utility/Task/TaskQueue';
import { tVector4 } from '../../../Utility/ArrayHelper';
import Point from '../../../Utility/Point';
import Renderable from '../../../Component/Renderable';
import { AwaitIterators, PlayAnimation } from '../AnimationHelper';
import { Counter } from './Counter';
import IQuestUpdateTaskData from '../../../State/Player/Quests/IQuestUpdateTaskData';

interface UpdateQuestProgressMeta {
    getNextUpdate: () => IQuestUpdateTaskData;
}

const trayOpenPivot: Point = new Point(1, 0.5);
const trayClosedPivot: Point = new Point(0, 0.5);
const hideQuestOffset: number = 300;
export class UpdateQuestProgress extends AnimationClip<
    UpdateQuestProgressMeta
> {
    public *execute(
        gameObject: GameObject,
        meta: UpdateQuestProgressMeta
    ): IterableIterator<number> {
        // Get next update data
        let nextUpdateData = meta.getNextUpdate();
        const trayElement = gameObject.getComponent(ElementComponent);
        // Initial delay
        yield* delay(750);

        // Open tray
        yield* this.showTray(trayElement);

        while (nextUpdateData) {
            const currentData = nextUpdateData;
            nextUpdateData = meta.getNextUpdate();
            yield* this.showQuestProgressUpdate(
                currentData,
                nextUpdateData !== undefined
            );
        }

        // Hide tray
        yield* this.showTray(trayElement, false);
        gameObject.children.forEach(child => child.destroy());
    }

    private createProgressElement(
        updateData: IQuestUpdateTaskData
    ): QuestButtonProgress {
        const progressElement = GameObject.Instantiate(QuestButtonProgress, {
            name: `quest_${updateData.questData.type}`,
            questData: updateData.questData,
            layout: {
                anchors: [0.5, 0.45, 0.5, 0.45],
                offsets: [-hideQuestOffset, 0, -hideQuestOffset, 0],
                pivot: [0.5, 0],
                scale: [0.85, 0.85],
            },
            alpha: 0,
        });
        return progressElement;
    }

    private createOffsetTween(
        element: ElementComponent,
        to: tVector4,
        duration: number = 450
    ): Tween {
        return this.getTween(element.offsets)
            .to(to, duration)
            .onUpdate(t => {
                try {
                    element.offsets = t;
                } catch (e) {
                    console.error(e);
                }
            })
            .easing(Easing.Quadratic.Out);
    }

    private createAlphaTween(
        element: Renderable,
        to: number,
        duration: number = 450
    ): Tween {
        return this.getTween(element)
            .to({ alpha: to }, duration)
            .easing(Easing.Quadratic.Out);
    }

    private createProgressBarTween(
        questObject: QuestButtonProgress,
        endProgress: number,
        duration: number = 450
    ): Tween {
        return this.getTween(questObject.fillNineSlice).to(
            {
                width: questObject.getWidthFromProgress(endProgress),
            },
            450
        );
    }

    private *slideQuestObject(
        questObject: QuestButtonProgress,
        offsets: tVector4,
        alpha: number
    ): IterableIterator<number> {
        let questOffsetTween = this.createOffsetTween(
            questObject.getComponent(ElementComponent),
            offsets
        );
        let questAlphaTween = this.createAlphaTween(
            questObject.getComponent(Renderable),
            alpha
        );
        yield* AwaitIterators([
            this.startTween(questOffsetTween),
            this.startTween(questAlphaTween),
        ]);
    }

    private *showQuestProgressUpdate(
        updateData: IQuestUpdateTaskData,
        hasMoreUpdates: boolean
    ): IterableIterator<number> {
        // Create element to display the data
        let questObject = this.createProgressElement(updateData);
        this.gameObject.addChild(questObject);

        // TODO: Fix setting width when creating the element
        if (updateData.newProgress !== 0) {
            questObject.fillNineSlice.visible = true;
            questObject.fillNineSlice.width = questObject.getWidthFromProgress(
                updateData.questData.progress
            );
        } else {
            questObject.fillNineSlice.visible = false;
        }

        // Slide out quest object
        yield* this.slideQuestObject(questObject, [0, 0, 0, 0], 1);

        let progressBarTween = this.createProgressBarTween(
            questObject,
            updateData.newProgress + updateData.questData.progress
        );

        yield* delay(350);
        // Fill progress bar + increment text
        yield* AwaitIterators([
            PlayAnimation(
                new Counter(questObject.progressBarText, {
                    startValue: updateData.questData.progress,
                    finalValue:
                        updateData.questData.progress + updateData.newProgress,
                    duration: 450,
                    onUpdateOverride: (count: number) => {
                        questObject.updateText(count);
                    },
                })
            ),
            this.startTween(progressBarTween),
        ]);
        yield* delay(500);

        if (hasMoreUpdates) {
            // Slide questObject to left and fade out
            yield* this.slideQuestObject(
                questObject,
                [hideQuestOffset, 0, hideQuestOffset, 0],
                0
            );
            questObject.destroy;
        }
    }

    private *showTray(
        element: ElementComponent,
        show: boolean = true,
        duration: number = 450
    ) {
        yield* this.startTween(
            this.getTween(element.pivot)
                .to(show ? trayOpenPivot : trayClosedPivot, duration)
                .easing(Easing.Quadratic.Out)
        );
    }
}
